import { motion } from 'framer-motion'
import { ExternalLink, Github } from 'lucide-react'

const Projects = () => {
  const projects = [
    {
      title: 'Smart Store Room Monitor',
      description: 'IoT-powered monitoring system with real-time alerts and cloud dashboard.',
      image: '/proj1.png',
      tags: ['IoT', 'Cloud', 'Mobile App'],
      category: 'IoT System',
      link: 'https://github.com/nahidislam106/Smart-store'
    },
    {
      title: 'Cyber Physical Laboratory',
      description: 'Modern website for research lab showcasing projects and publications.',
      image: '/proj2.png',
      tags: ['Web Design', 'React', 'CMS'],
      category: 'Website',
      link: 'https://www.cplab.org/'
    },
    {
      title: 'Real Time Messenger',
      description: 'Cross-platform messaging app with end-to-end encryption.',
      image: '/proj3.png',
      tags: ['React Native', 'Firebase', 'WebSocket'],
      category: 'Mobile App',
      link: '#'
    },
    {
      title: 'Hospital Management System',
      description: 'Comprehensive healthcare management platform for hospitals and clinics.',
      image: '/hospitl_management_system.png',
      tags: ['Full-Stack', 'Database', 'Healthcare'],
      category: 'Web Application',
      link: 'https://github.com/nahidislam106/Hospital-Management-System'
    },
    {
      title: 'Advanced Weather Station',
      description: 'ESP32-based weather monitoring with DHT11 temperature/humidity sensor and rain detection.',
      image: '/weater_station.png',
      tags: ['ESP32', 'IoT', 'Sensors'],
      category: 'IoT System',
      link: 'https://github.com/nahidislam106/Weather-Station-Pro'
    },
    {
      title: 'Smart Toll Management System',
      description: 'Automated toll collection system with Google Sheets Integration for data management.',
      image: '/smart_toll_management.png',
      tags: ['IoT', 'Google Sheets', 'Automation'],
      category: 'IoT System',
      link: 'https://github.com/nahidislam106/Smart-toll-system'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 }
    }
  }

  return (
    <section id="projects" className="py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
              Featured Projects
            </span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Recent innovations and prototypes bringing ideas to life.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {projects.map((project, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              className="glass-card overflow-hidden group"
            >
              <div className="relative overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60"></div>
                <div className="absolute top-4 right-4 px-3 py-1 bg-blue-500/90 backdrop-blur-sm rounded-full text-xs font-medium">
                  {project.category}
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2 group-hover:text-blue-400 transition-colors">
                  {project.title}
                </h3>
                <p className="text-sm text-white/70 mb-4 leading-relaxed">
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 text-xs bg-white/5 border border-white/10 rounded-full text-white/80"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex gap-3">
                  <motion.a
                    href={project.link}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-sm font-medium transition-colors"
                  >
                    <ExternalLink size={16} />
                    View Project
                  </motion.a>
                  <motion.a
                    href="#"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center justify-center p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Github size={18} />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default Projects
